<template>
  <div>
    <GeojsonLayer
      :layer="layerStyleCluster"
      :source="source"
      :sourceId="sourceId"
      @layerClicked="onClusterClicked"
      @layerMouseEnter="handleMouseMoveCluster"
      @layerMouseMove="handleMouseMoveCluster"
      @layerMouseLeave="handleMouseLeaveCluster"
    />
  </div>
</template>
<!-- eslint-disable vue/one-component-per-file -->
<script>
import { mapState, mapGetters } from 'vuex'
import mapboxgl from 'mapbox-gl'
import { MAPVIEW_PREFIX } from '@/store/module-prefixes'
import GeojsonLayer from '@/components/map/mapbox-layers/GeojsonLayer.vue'
import MapPopupNew from '../../popups/MapPopupNew.vue'
import { createSmallTitle } from '@/components/Builder'
import { createApp } from 'vue'
import { zoomToEntities } from '@/composables/useMapbox'
import i18n from '@/i18n'
import * as turf from '@turf/turf'
import { isValidFeature } from '@/utils/isValidFeature'

export default {
  name: 'LayerBuildingMarkerCluster',
  components: {
    GeojsonLayer
  },
  props: {
    buildingSource: {
      validator: (prop) => typeof prop === 'object' || prop == null,
      default: null
    },
    complexSource: {
      validator: (prop) => typeof prop === 'object' || prop == null,
      default: null
    },
    layerId: {
      type: String,
      required: true
    }
  },
  emits: ['hover'],
  data() {
    return {
      hoveredClusterId: '',
      hoveredClusterIds: [],
      hoveredClusterNames: [],
      popupTitle: '',
      popupCluster: new mapboxgl.Popup({ closeButton: false, offset: 75 }),
      sourceId: 'source-building-marker-cluster',
      visibilityLimited: ['interpolate', ['linear'], ['zoom'], 6, 0, 7, 1, 14, 1, 15, 0],
      visibilityStandard: ['interpolate', ['linear'], ['zoom'], 14, 1, 15, 0]
    }
  },
  computed: {
    ...mapGetters({
      isMeasurementActive: 'isMeasurementActive'
    }),
    ...mapGetters(MAPVIEW_PREFIX, {
      selectedBuildingName: 'selectedBuildingName'
    }),
    ...mapState({
      clickedItemType: (state) => state.clickedItemType,
      mobileScreen: (state) => state.mobileScreen
    }),
    isAllBuildingsSelected() {
      return this.selectedBuildingName == 'Alla' && this.clickedItemType == 'building'
    },
    isBuildingOrMoreDetailedSelected() {
      return (
        ['room', 'poi', 'line', 'zone', 'level', 'building'].indexOf(this.clickedItemType) !== -1
      )
    },
    isComplexOrMoreDetailedSelected() {
      // Check for all types of (currently) possible markers
      return ['building', 'realEstate', 'complex'].indexOf(this.clickedItemType) !== -1
    },
    isVisibilityThresholdReached() {
      return this.complexSource?.data.features.length > 10
    },
    layerStyleCluster() {
      return {
        id: this.layerId,
        source: this.sourceId,
        type: 'symbol',
        maxzoom: 14,
        filter: ['has', 'point_count'],
        layout: {
          'icon-image': [
            'case',
            ['boolean', ['==', ['get', 'cluster_id'], this.hoveredClusterId], false],
            'complex_marker_missing_id_shadow_active_large',
            ['all', this.isAllBuildingsSelected, true],
            'complex_marker_missing_id_shadow_active_large',
            ['all', ['==', this.isBuildingOrMoreDetailedSelected, true]] && [
              'all',
              ['==', this.isComplexOrMoreDetailedSelected, true]
            ],
            'complex_marker_shadow_missing_id_inactive_large',
            'complex_marker_missing_id_shadow_active_large'
          ],
          'icon-size': ['step', ['get', 'point_count'], 1, 1, 0.65, 2, 0.65, 5, 0.8],
          'icon-allow-overlap': true,
          'icon-anchor': 'bottom',
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 16,
          'text-allow-overlap': true,
          'text-offset': [
            'step',
            ['get', 'point_count'],
            ['literal', [0, -2.75]],
            5,
            ['literal', [0, -3.35]]
          ]
        },
        paint: {
          'icon-opacity': this.isVisibilityThresholdReached
            ? this.visibilityLimited
            : this.visibilityStandard,
          'text-color': 'white',
          'text-opacity': this.isVisibilityThresholdReached
            ? this.visibilityLimited
            : this.visibilityStandard
        }
      }
    },
    source() {
      const buildingMarkerSource = {
        type: 'geojson',
        data: {
          id: this.sourceId,
          type: 'FeatureCollection',
          features: []
        },
        cluster: true,
        clusterMaxZoom: 13,
        clusterRadius: 30
      }
      if (this.buildingSource) {
        this.buildingSource.data.features.forEach((building) => {
          if (!isValidFeature(building)) return
          const coordinates = building.properties.location
            ? building.properties.location.coordinates.slice(0, 2)
            : turf.centerOfMass(building).geometry.coordinates
          buildingMarkerSource.data.features.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates
            },
            id: building.properties.id,
            properties: {
              id: building.properties.id,
              title: building.properties.displayName
            }
          })
        })
      }
      return buildingMarkerSource
    }
  },
  methods: {
    onClusterClicked(event) {
      if (this.isMeasurementActive) return
      let renderedFeatures = this.$store.map.queryRenderedFeatures(event.point)
      if (renderedFeatures[0].properties.cluster) {
        let clusterLayer = this.$store.map.queryRenderedFeatures(event.point, {
          layers: [this.layerId]
        })
        let clusterId = clusterLayer[0].properties.cluster_id
        let pointCount = clusterLayer[0].properties.point_count
        let clusterSource = this.$store.map.getSource(this.source.data.id)
        clusterSource.getClusterLeaves(clusterId, pointCount, 0, (err, features) => {
          if (err) {
            return
          }

          zoomToEntities(features.map((f) => f.id))
        })
      }
    },
    isFirstLayer(event) {
      const renderedFeatures = this.$store.map.queryRenderedFeatures(event.point)
      return (
        renderedFeatures[0].layer.id === this.layerId &&
        renderedFeatures[0].layer.paint['icon-opacity']
      )
    },
    setCursor(cursorType) {
      this.$store.map.getCanvas().style.cursor = cursorType
    },
    handleMouseMoveCluster(event) {
      if (!this.isMeasurementActive) {
        if (!this.isFirstLayer(event)) {
          this.popupCluster.remove()
          this.hoveredClusterId = null
          this.setCursor('')
        }
        if (this.isFirstLayer(event)) {
          this.setCursor('pointer')
          let clusterLayer = this.$store.map.queryRenderedFeatures(event.point, {
            layers: [this.layerId]
          })
          let coord = clusterLayer[0].geometry.coordinates
          let clusterId = clusterLayer[0].properties.cluster_id
          if (clusterId !== this.hoveredClusterId) {
            this.hoveredClusterId = clusterId
            let clusterSource = this.$store.map.getSource(this.sourceId)
            this.hoveredClusterNames = []
            this.hoveredClusterIds = []
            this.fetchClusterData(clusterId, clusterSource, event, coord)
          }
        }
      }
    },
    fetchClusterData(clusterId, clusterSource, event, coord) {
      this.hoveredClusterIds.shift()
      clusterSource.getClusterChildren(clusterId, (err, features) => {
        if (err) {
          return err
        } else {
          for (let x in features) {
            if (features[x].properties.title) {
              this.hoveredClusterNames.push(features[x].properties.title)
            }
            if (features[x].properties.cluster_id) {
              this.hoveredClusterIds.push(features[x].properties.cluster_id)
            }
          }
          if (this.hoveredClusterIds.length) {
            this.fetchClusterData(this.hoveredClusterIds[0], clusterSource, event, coord)
          } else {
            this.updateHoverCluster(event, this.hoveredClusterNames, clusterId, coord)
          }
        }
      })
    },
    updateHoverCluster(event, data, clusterId, coord) {
      if (data && this.hoveredClusterId) {
        this.showPopupCluster(event, data, coord)
      } else {
        this.popupCluster.remove()
        this.setCursor('')
      }
    },
    showPopupCluster(event, data, coord) {
      let sorted = data.sort()
      const rows = []
      for (let i = 0; i < sorted.length; i++) {
        rows.push(createSmallTitle(sorted[i]))
        if (i === 4 && i !== sorted.length - 1) {
          rows.push(createSmallTitle('m.fl.'))
          break
        }
      }
      const mountPoint = document.createElement('div')
      const instance = createApp(MapPopupNew, { rows })
      instance.use(i18n)
      instance.mount(mountPoint)
      this.popupCluster.setLngLat(coord).setDOMContent(mountPoint).addTo(this.$store.map)
    },
    handleMouseLeaveCluster(event) {
      if (!this.isMeasurementActive) {
        this.hoveredClusterId = null
        this.setCursor('')
        this.updateHoverCluster(event)
      }
    }
  }
}
</script>
